import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  FolderOpen, 
  Plus, 
  ArrowRight, 
  Clock, 
  TrendingUp,
  Building2,
  MessageSquare,
  Brain,
  FolderArchive,
  UserCircle,
  Receipt,
  Euro
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard({ user, setUser }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(function() {
    fetchStats();
  }, []);

  const fetchStats = async function() {
    try {
      const response = await fetch(API_URL + "/api/stats", {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = function(dateString) {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const hasOrg = Boolean(user && user.organization_id);
  const recentContracts = stats?.recent_contracts || [];

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8" data-testid="dashboard-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-semibold tracking-tight mb-2">
            Bonjour, {user?.name ? user.name.split(' ')[0] : 'Utilisateur'}
          </h1>
          <p className="text-muted-foreground">
            Bienvenue sur votre espace de gestion contractuelle
          </p>
        </div>

        {!hasOrg && (
          <Card className="mb-8 border-dashed border-2 bg-muted/30" data-testid="setup-org-card">
            <CardContent className="flex items-center justify-between p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Configurez votre organisation</h3>
                  <p className="text-sm text-muted-foreground">
                    Créez votre organisation pour commencer à gérer vos contrats
                  </p>
                </div>
              </div>
              <Button onClick={function() { navigate('/organization'); }} data-testid="setup-org-btn">
                Configurer
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-8">
          <Card className="card-hover" data-testid="clients-stat-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Clients</CardTitle>
              <UserCircle className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.clients_count || 0}</div>
            </CardContent>
          </Card>
          <Card className="card-hover" data-testid="cases-stat-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Dossiers</CardTitle>
              <FolderArchive className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.cases_count || 0}</div>
            </CardContent>
          </Card>
          <Card className="card-hover" data-testid="contracts-stat-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Contrats</CardTitle>
              <FileText className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.contracts_count || 0}</div>
            </CardContent>
          </Card>
          <Card className="card-hover" data-testid="revenue-stat-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Revenus</CardTitle>
              <Euro className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{(stats?.total_revenue || 0).toFixed(0)} EUR</div>
              {(stats?.pending_amount || 0) > 0 && (
                <p className="text-xs text-amber-600">{stats.pending_amount.toFixed(0)} EUR en attente</p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card data-testid="quick-actions-card">
            <CardHeader>
              <CardTitle className="text-lg">Actions rapides</CardTitle>
              <CardDescription>Commencez à travailler sur vos contrats</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start gap-3 h-auto py-4"
                onClick={function() { navigate('/templates/upload'); }}
                disabled={!hasOrg}
                data-testid="upload-template-action"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Plus className="w-5 h-5 text-primary" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Nouveau modèle</div>
                  <div className="text-xs text-muted-foreground">
                    Importez un modèle Word (.docx)
                  </div>
                </div>
              </Button>

              <Button 
                variant="outline" 
                className="w-full justify-start gap-3 h-auto py-4"
                onClick={function() { navigate('/templates'); }}
                data-testid="create-contract-action"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Créer un contrat</div>
                  <div className="text-xs text-muted-foreground">
                    À partir d'un modèle existant
                  </div>
                </div>
              </Button>

              <Button 
                variant="outline" 
                className="w-full justify-start gap-3 h-auto py-4"
                onClick={function() { navigate('/contracts'); }}
                data-testid="view-contracts-action"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <FolderOpen className="w-5 h-5 text-primary" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Voir mes contrats</div>
                  <div className="text-xs text-muted-foreground">
                    Gérer les contrats existants
                  </div>
                </div>
              </Button>

              <Button 
                variant="outline" 
                className="w-full justify-start gap-3 h-auto py-4"
                onClick={function() { navigate('/ai/chat'); }}
                data-testid="ai-chat-action"
              >
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Assistant IA</div>
                  <div className="text-xs text-muted-foreground">
                    Générer des contrats avec GPT-5.2
                  </div>
                </div>
              </Button>

              <Button 
                variant="outline" 
                className="w-full justify-start gap-3 h-auto py-4"
                onClick={function() { navigate('/ai/analysis'); }}
                data-testid="ai-analysis-action"
              >
                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                  <Brain className="w-5 h-5 text-purple-600" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Analyse IA</div>
                  <div className="text-xs text-muted-foreground">
                    Analyser des documents avec Claude
                  </div>
                </div>
              </Button>
            </CardContent>
          </Card>

          <Card data-testid="recent-contracts-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Contrats récents</CardTitle>
                <CardDescription>Dernière activité sur vos contrats</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={function() { navigate('/contracts'); }}>
                Voir tout
              </Button>
            </CardHeader>
            <CardContent>
              {recentContracts.length > 0 ? (
                <div className="space-y-4">
                  {recentContracts.map(function(contract, index) {
                    return (
                      <div 
                        key={contract.contract_id || index}
                        className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={function() { navigate('/contracts/' + contract.contract_id); }}
                        data-testid={"recent-contract-" + (contract.contract_id || index)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div>
                            <div className="font-medium text-sm">{contract.name}</div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(contract.created_at)}
                            </div>
                          </div>
                        </div>
                        <Badge variant="secondary" className="capitalize">
                          v{contract.version}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Aucun contrat récent
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={function() { navigate('/templates'); }}
                  >
                    Créer un contrat
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
